/**
 * Merges a model's opaque meshes into one vertex-coloured mesh, keeping animated parts separate
 * (concept L552–L565). One draw call per enemy instead of dozens (GDD §10.3: geometry merging).
 */
import { Matrix4, Mesh, MeshStandardMaterial, type Material, type Object3D } from 'three';
import { merge, type Part } from '../../world/geometry.ts';

export function bake<T extends Object3D>(root: T, keep: Object3D[], mat: Material): T {
  root.updateMatrixWorld(true);
  const inv = new Matrix4().copy(root.matrixWorld).invert();
  const parts: Part[] = [];
  const rm: Mesh[] = [];
  root.traverse((o) => {
    if (!(o instanceof Mesh)) return;
    for (let p: Object3D | null = o; p && p !== root; p = p.parent) if (keep.includes(p)) return;
    const m = o.material as Material & { color?: MeshStandardMaterial['color'] };
    if (!m || m.transparent || !m.color) return;
    const g = o.geometry.clone();
    g.applyMatrix4(new Matrix4().multiplyMatrices(inv, o.matrixWorld));
    parts.push({ geo: g, color: m.color });
    rm.push(o);
  });
  for (const o of rm) o.parent?.remove(o);
  const bm = new Mesh(merge(parts), mat);
  bm.castShadow = true;
  bm.receiveShadow = true;
  root.add(bm);
  return root;
}
